import React, { useEffect, useMemo, useState } from 'react';
import { normalizePartnerFieldErrors } from '../../Utils/partnerApiFeedback';

const EMPTY_FORM = {
  csn: '',
  companyName: '',
  typeOfPartner: '',
  partnerAdoptionEngineer: '',
  tierStatus: '',
  serviceLevel: '',
};

const buildSelectOptions = (currentValue, options = []) => {
  const current = currentValue?.trim();
  if (!current || options.includes(current)) {
    return options;
  }
  return [current, ...options];
};

const PartnerCompanyEditForm = ({
  company,
  open,
  saving,
  mode = 'edit',
  partnerAdoptionEngineerOptions = [],
  tierTypeOptions = [],
  serviceLevelOptions = [],
  formOptionsLoading = false,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const isCreateMode = mode === 'create';

  useEffect(() => {
    if (!open) {
      return;
    }

    if (isCreateMode) {
      setForm(EMPTY_FORM);
      setFieldErrors({});
      return;
    }

    if (!company) {
      return;
    }

    setForm({
      csn: company.csn || '',
      companyName: company.companyName || '',
      typeOfPartner: company.typeOfPartner || '',
      partnerAdoptionEngineer: company.partnerAdoptionEngineer || '',
      tierStatus: company.tierStatus || '',
      serviceLevel: company.serviceLevel || '',
    });
    setFieldErrors({});
  }, [company, isCreateMode, open]);

  const tierStatusSelectOptions = useMemo(
    () => buildSelectOptions(form.tierStatus, tierTypeOptions),
    [form.tierStatus, tierTypeOptions],
  );

  const serviceLevelSelectOptions = useMemo(
    () => buildSelectOptions(form.serviceLevel, serviceLevelOptions),
    [form.serviceLevel, serviceLevelOptions],
  );

  const partnerAdoptionEngineerSelectOptions = useMemo(
    () => buildSelectOptions(form.partnerAdoptionEngineer, partnerAdoptionEngineerOptions),
    [form.partnerAdoptionEngineer, partnerAdoptionEngineerOptions],
  );

  if (!open || (!isCreateMode && !company)) {
    return null;
  }

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setFieldErrors((current) => ({ ...current, [field]: '' }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFieldErrors({});

    try {
      await onSave(form);
    } catch (saveError) {
      const nextFieldErrors = normalizePartnerFieldErrors(saveError?.fieldErrors);
      if (Object.keys(nextFieldErrors).length > 0) {
        setFieldErrors(nextFieldErrors);
      }
    }
  };

  const renderSelectField = (field, label, options, placeholder, loading = false) => (
    <div className="partner-company-form-field" key={field}>
      <label htmlFor={`partner-company-${field}`}>{label}</label>
      <select
        id={`partner-company-${field}`}
        className={`partner-form-select form-select ${fieldErrors[field] ? 'is-invalid' : ''}`}
        value={form[field]}
        onChange={handleChange(field)}
        disabled={saving || loading}
      >
        <option value="">{loading ? 'Loading options...' : placeholder}</option>
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
      {fieldErrors[field] ? <div className="invalid-feedback d-block">{fieldErrors[field]}</div> : null}
    </div>
  );

  const renderField = (field, label, placeholder) => (
    <div className="partner-company-form-field" key={field}>
      <label htmlFor={`partner-company-${field}`}>{label}</label>
      <input
        id={`partner-company-${field}`}
        className={`form-control ${fieldErrors[field] ? 'is-invalid' : ''}`}
        value={form[field]}
        onChange={handleChange(field)}
        placeholder={placeholder}
        disabled={saving}
      />
      {fieldErrors[field] ? <div className="invalid-feedback d-block">{fieldErrors[field]}</div> : null}
    </div>
  );

  return (
    <div className="partner-company-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="partner-company-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="partner-company-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="partner-company-modal-header">
          <div>
            <h3 id="partner-company-modal-title">
              {isCreateMode ? 'Add partner company' : 'Edit partner company'}
            </h3>
            <p>{isCreateMode ? 'Create a new partner company record' : (company.companyName || 'Update company details')}</p>
          </div>
          <button type="button" className="partner-company-modal-close" onClick={onClose} disabled={saving}>
            ×
          </button>
        </header>

        <form className="partner-company-modal-body" onSubmit={handleSubmit}>
          <div className="partner-company-form-grid">
            {renderField('csn', 'CSN', 'e.g. 20020598')}
            {renderField('companyName', 'Company name', 'e.g. ANS Digital Transformation Limited')}
            {renderField('typeOfPartner', 'Type of partner', 'e.g. VAR')}
            {renderSelectField(
              'partnerAdoptionEngineer',
              'Partner adoption engineer',
              partnerAdoptionEngineerSelectOptions,
              'Select partner adoption engineer',
              formOptionsLoading,
            )}
            {renderSelectField(
              'tierStatus',
              'Tier status',
              tierStatusSelectOptions,
              'Select tier status',
              formOptionsLoading,
            )}
            {renderSelectField(
              'serviceLevel',
              'Service level',
              serviceLevelSelectOptions,
              'Select service level',
              formOptionsLoading,
            )}
          </div>

          <footer className="partner-company-modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (isCreateMode ? 'Add company' : 'Save changes')}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default PartnerCompanyEditForm;
